<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">

          <v-card class="pa-4">
            <div class="d-flex justify-space-between mb-4">
              <h2 class="text-h6 font-weight-bold">Budget Head List</h2>

              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi mdi-plus"
                rounded="xl"
                @click="openCreate"
              >
                New Budget Head
              </v-btn>
            </div>

            <v-data-table
              :headers="headers"
              :items="budgetHeadList"
              class="elevation-1"
            >

              <template #item.index="{ index }">
                {{ ++index }}
              </template>

              <template #item.budgetType="{ item }">
                {{ item.budget_type?.name }}
              </template>

              <template #item.status="{ item }">
                <v-chip
                  v-if="item.status == 1"
                  color="success"
                  size="small"
                >
                  Active
                </v-chip>
                <v-chip v-else color="red" size="small">
                  Inactive
                </v-chip>
              </template>

              <template #item.actions="{ item }">
                <v-icon
                  class="me-2"
                  color="primary"
                  @click="editBudgetHead(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>

            </v-data-table>
          </v-card>

        </v-col>
      </v-row>

      <!-- MODAL -->
      <v-dialog v-model="dialog" max-width="500">
        <v-card class="pa-3">

          <v-card-title>
            {{ isEdit ? "Update Budget Head" : "Create Budget Head" }}
          </v-card-title>

          <v-card-text>
            <v-row>

            <v-col cols="12">
                <v-select
                  v-model="budget_type_id"
                  :items="budgetTypeList"
                  item-title="name"
                  item-value="id"
                  label="Budget Type *"
                  variant="outlined"
                  density="compact"
                  :error-messages="budgetTypeError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="name"
                  label="Name *"
                  variant="outlined"
                  density="compact"
                  :error-messages="nameError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  label="Active"
                  v-model="status"
                  :true-value="1"
                  :false-value="0"
                />
              </v-col>

            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-actions>
                <v-spacer />
                <v-btn class="bg-red" @click="dialog=false"> Close </v-btn>
                <v-btn class="bg-brand w-15 ms-2" @click="saveBudgetHead">  {{ isEdit ? "Update" : "Create" }} </v-btn>
          </v-card-actions>

        </v-card>
      </v-dialog>

    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { useStore } from "vuex"

import { useForm, useField } from "vee-validate"
import * as yup from "yup"

const store = useStore()

/* -----------------------------
STATE
--------------------------------*/
const dialog = ref(false)
const isEdit = ref(false)
const budgetHeadId = ref(null)

/* -----------------------------
VALIDATION
--------------------------------*/
const schema = yup.object({
  name: yup.string().required("Name is required"),
  budget_type_id: yup
    .number()
    .required("Budget type is required")
    .typeError("Budget type is required"),
  status: yup.number()
})

const { handleSubmit, setErrors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    name: "",
    budget_type_id: null,
    status: 1
  }
})

const { value: name, errorMessage: nameError } = useField("name")
const { value: budget_type_id, errorMessage: budgetTypeError } = useField("budget_type_id")
const { value: status } = useField("status")

/* -----------------------------
TABLE
--------------------------------*/
const headers = [
  { title: "#SL", key: "index" },
  { title: "Budget Type", key: "budgetType" },
  { title: "Code", key: "code" },
  { title: "Name", key: "name" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions" }
]

const budgetHeadList = computed(() => {
  return store.getters.getBudgetHeadList
})

const budgetTypeList = computed(() => {
  return store.getters.getBudgetTypeList
})

/* -----------------------------
LOAD DATA
--------------------------------*/
const loadBudgetHeads = async () => {
  store.commit("SET_LOADING", { color: "primary" })
  await store.dispatch("getBudgetHeadList")
  store.commit("UNSET_LOADING")
}

const loadBudgetTypes = async () => {
  await store.dispatch("getBudgetTypeList")
}

/* -----------------------------
CREATE
--------------------------------*/
const openCreate = () => {
  isEdit.value = false

  resetForm({
    values: {
      name: "",
      budget_type_id: null,
      status: 1
    }
  })

  dialog.value = true
}

/* -----------------------------
EDIT
--------------------------------*/
const editBudgetHead = async (item) => {
  isEdit.value = true
  budgetHeadId.value = item.id

  const response = await store.dispatch("getBudgetHeadById", item.id)

  const data = response.data

  name.value = data.name
  budget_type_id.value = data.budget_type_id
  status.value = data.status

  dialog.value = true
}

/* -----------------------------
PAYLOAD
--------------------------------*/
const buildPayload = () => {
  return {
    name: name.value,
    budget_type_id: budget_type_id.value,
    status: status.value
  }
}

/* -----------------------------
SAVE
--------------------------------*/
const saveBudgetHead = handleSubmit(async () => {
  try {
    const payload = buildPayload()

    if (isEdit.value) {
      payload.id = budgetHeadId.value
      await store.dispatch("updateBudgetHead", payload)
    } else {
      await store.dispatch("saveBudgetHead", payload)
    }

    dialog.value = false
    loadBudgetHeads()

  } catch (error) {
    if (error.response && error.response.status === 422) {
      setErrors(error.response.data.errors)
    }
  }
})

/* -----------------------------
MOUNT
--------------------------------*/
onMounted(() => {
  loadBudgetTypes()
  loadBudgetHeads()
})
</script>